import React, { useCallback, useEffect, useRef, useState } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormInput,
  FormSelect,
  Row
} from 'shards-react';
import PageTitle from '../../../../components/PageTitle';
import { useUrlState } from '../../../../hooks/useUrlState';
import ScraperDocumentsService, {
  API_LIST_LIMIT,
  PAGE_SIZE
} from '../../../../services/ScraperDocumentsService';
import Translator from '../../../../services/Translator';
import { documentFileName } from '../../../../utils/scraperDocuments';
import { dateOnlyParam, positiveIntegerParam, stringParam } from '../../../../utils/urlStateParams';
import PipelineBar from './PipelineBar';
import { apiSegments, groupBy, parserSegments } from './pipelineStatus';

const tableStyle = { width: '100%', background: '#fff', borderCollapse: 'collapse' };
const cellStyle = { borderBottom: '1px solid #e1e5eb', padding: '8px', verticalAlign: 'middle' };
const headerCellStyle = { ...cellStyle, fontWeight: 600, background: '#f8f9fa' };
const monoStyle = { fontFamily: 'monospace', fontSize: '12px', overflowWrap: 'anywhere' };
const mutedStyle = { color: '#6c757d', fontSize: '12px' };

const EMPTY_PIPELINE = {
  parserDocuments: null,
  parserFails: null,
  payments: null,
  paymentFails: null,
  truncated: false,
  errors: {}
};

// Pipeline collections are stored grouped by scraper document id (`{ [id]: [...] }`).
const READY_PIPELINE = {
  ...EMPTY_PIPELINE,
  parserDocuments: {},
  parserFails: {},
  payments: {},
  paymentFails: {}
};

const FILTER_KEYS = ['accountId', 'credentialId', 'dateFrom', 'dateTo'];
const AVAILABLE_CREDENTIAL_STATUSES = ['ok', 'expired', 'blocked', 'pending', 'incorrect'];
const URL_STATE_CONFIG = {
  accountId: stringParam(),
  credentialId: stringParam(),
  dateFrom: dateOnlyParam(),
  dateTo: dateOnlyParam(),
  page: positiveIntegerParam()
};

const toGraphqlFilter = (filters) => {
  const filter = {};
  FILTER_KEYS.forEach((key) => {
    if (filters[key]) filter[key] = filters[key];
  });
  return filter;
};

const filtersFromUrlState = (urlState) =>
  Object.fromEntries(FILTER_KEYS.map((key) => [key, urlState[key]]));

const formatDate = (value, pattern = 'dd/MM/yyyy') => {
  if (!value) return '-';
  const parsed = parseISO(String(value));
  return isValid(parsed) ? format(parsed, pattern) : String(value);
};

const shortId = (id) => (id ? `${id.slice(0, 8)}…` : '-');

const fileNameCellStyle = { ...cellStyle, maxWidth: '280px', overflowWrap: 'anywhere' };

export default function ScraperDocuments() {
  const [urlState, setUrlState] = useUrlState(URL_STATE_CONFIG);
  const [filters, setFilters] = useState(() => filtersFromUrlState(urlState));
  const [documents, setDocuments] = useState(null);
  const [pipeline, setPipeline] = useState(EMPTY_PIPELINE);
  const [credentials, setCredentials] = useState({ accountId: null, items: [], byId: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestId = useRef(0);
  const credentialsAccountId = useRef(null);
  const page = urlState.page;

  const loadCredentials = useCallback((accountId) => {
    if (credentialsAccountId.current === accountId) return;
    credentialsAccountId.current = accountId;

    if (!accountId) {
      setCredentials({ accountId: null, items: [], byId: {} });
      return;
    }

    setCredentials({ accountId, items: [], byId: {} });
    ScraperDocumentsService()
      .getCredentials({
        accountNameOrId: accountId,
        credentialStatus: AVAILABLE_CREDENTIAL_STATUSES
      })
      .then((items) => {
        const byId = {};
        items.forEach((credential) => {
          byId[credential.id] = credential;
        });
        if (credentialsAccountId.current === accountId) {
          setCredentials({ accountId, items, byId });
        }
      });
  }, []);

  const loadPipeline = useCallback((ids, currentRequest) => {
    if (!ids.length) {
      setPipeline(READY_PIPELINE);
      return;
    }
    setPipeline(EMPTY_PIPELINE);
    const service = ScraperDocumentsService();
    const update = (changes) => {
      if (requestId.current !== currentRequest) return;
      setPipeline((current) => ({
        ...current,
        ...changes,
        truncated: current.truncated || Boolean(changes.truncated),
        errors: { ...current.errors, ...(changes.errors || {}) }
      }));
    };
    const fail = (key, message) => () => update({ [key]: {}, errors: { [key]: message } });

    service
      .getParserDocuments(ids)
      .then((items) => update({ parserDocuments: groupBy(items, 'scrapedDocumentId') }))
      .catch(fail('parserDocuments', 'No se pudieron obtener los parser docs'));
    service
      .getParserFails(ids)
      .then((items) => update({ parserFails: groupBy(items, 'scrapedDocumentId') }))
      .catch(fail('parserFails', 'No se pudieron obtener los parser fails'));
    service
      .getPayments(ids)
      .then(({ items, truncated }) =>
        update({ payments: groupBy(items, 'scrapedDocumentExtId'), truncated })
      )
      .catch(fail('payments', 'No se pudieron obtener los payments'));
    service
      .getPaymentFails(ids)
      .then(({ items, truncated }) =>
        update({ paymentFails: groupBy(items, 'scrapedDocumentExtId'), truncated })
      )
      .catch(fail('paymentFails', 'No se pudieron obtener los payment fails'));
  }, []);

  // Keep editable form values separate from the applied URL state until the user searches.
  useEffect(() => {
    setFilters(filtersFromUrlState(urlState));
  }, [urlState.accountId, urlState.credentialId, urlState.dateFrom, urlState.dateTo]);

  // Direct links and browser back/forward update the applied state and rerun the search.
  useEffect(() => {
    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;
    loadCredentials(urlState.accountId);

    if (!urlState.accountId) {
      setDocuments(null);
      setPipeline(EMPTY_PIPELINE);
      setError(null);
      setLoading(false);
      return () => {
        if (requestId.current === currentRequest) requestId.current += 1;
      };
    }

    setLoading(true);
    setError(null);
    setDocuments(null);
    setPipeline(EMPTY_PIPELINE);

    ScraperDocumentsService()
      .getDocuments(toGraphqlFilter(urlState), (urlState.page - 1) * PAGE_SIZE)
      .then((result) => {
        if (requestId.current !== currentRequest) return;
        setDocuments(result);
        loadPipeline(
          result.map((document) => document.id),
          currentRequest
        );
      })
      .catch(() => {
        if (requestId.current === currentRequest) {
          setError('No se pudieron obtener los documentos del scraper.');
        }
      })
      .finally(() => {
        if (requestId.current === currentRequest) setLoading(false);
      });

    return () => {
      if (requestId.current === currentRequest) requestId.current += 1;
    };
  }, [
    loadCredentials,
    loadPipeline,
    urlState.accountId,
    urlState.credentialId,
    urlState.dateFrom,
    urlState.dateTo,
    urlState.page
  ]);

  const setFilter = (key) => (event) => {
    const value = event.target.value.trim();
    setFilters((current) => {
      const next = { ...current, [key]: value };
      // Credentials belong to an account: drop the selection when the account changes.
      if (key === 'accountId' && value !== current.accountId) next.credentialId = '';
      return next;
    });
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setUrlState({ ...filters, page: 1 });
  };

  const forDocument = (key, id) => (pipeline[key] === null ? null : pipeline[key][id] || []);
  const credentialsById = credentials.byId;

  const parserError = pipeline.errors.parserDocuments || pipeline.errors.parserFails;
  const apiError = pipeline.errors.payments || pipeline.errors.paymentFails;
  const credentialsForAccount =
    credentials.accountId === filters.accountId ? credentials.items : [];

  return (
    <Container className="main-content-container px-4" fluid>
      <Row className="page-header py-4" noGutters>
        <PageTitle
          className="text-sm-left"
          sm="4"
          subtitle="Cuentas"
          title="Documentos de Scraper"
        />
      </Row>

      <Card className="mb-4" small>
        <CardBody>
          <form onSubmit={onSubmit}>
            <Row form>
              <Col className="mb-2" lg="3" md="6">
                <label htmlFor="scraperDocumentsAccountId">Account ID *</label>
                <FormInput
                  id="scraperDocumentsAccountId"
                  onBlur={() => loadCredentials(filters.accountId)}
                  onChange={setFilter('accountId')}
                  placeholder="Id de la cuenta"
                  value={filters.accountId}
                />
              </Col>
              <Col className="mb-2" lg="3" md="6">
                <label htmlFor="scraperDocumentsCredentialId">Credencial</label>
                <FormSelect
                  disabled={!filters.accountId}
                  id="scraperDocumentsCredentialId"
                  onChange={setFilter('credentialId')}
                  value={filters.credentialId}
                >
                  <option value="">Todas</option>
                  {filters.credentialId && !credentialsById[filters.credentialId] && (
                    <option value={filters.credentialId}>{filters.credentialId}</option>
                  )}
                  {credentialsForAccount.map((credential) => (
                    <option key={credential.id} value={credential.id}>
                      {credential.infoProvider} · {credential.username}
                    </option>
                  ))}
                </FormSelect>
              </Col>
              <Col className="mb-2" lg="2" md="4">
                <label htmlFor="scraperDocumentsDateFrom">Fecha desde</label>
                <FormInput
                  id="scraperDocumentsDateFrom"
                  onChange={setFilter('dateFrom')}
                  type="date"
                  value={filters.dateFrom}
                />
              </Col>
              <Col className="mb-2" lg="2" md="4">
                <label htmlFor="scraperDocumentsDateTo">Fecha hasta</label>
                <FormInput
                  id="scraperDocumentsDateTo"
                  onChange={setFilter('dateTo')}
                  type="date"
                  value={filters.dateTo}
                />
              </Col>
              <Col className="mb-2 d-flex align-items-end" lg="2" md="4">
                <Button block disabled={!filters.accountId || loading} type="submit">
                  {loading ? 'Buscando...' : 'Buscar'}
                </Button>
              </Col>
            </Row>
          </form>
        </CardBody>
      </Card>

      {error && <Alert theme="danger">{error}</Alert>}
      {pipeline.truncated && (
        <Alert theme="warning">
          Se alcanzó el límite de {API_LIST_LIMIT} payments para esta página; las barritas de API
          pueden estar incompletas. Usá Document Trace para ver el detalle.
        </Alert>
      )}

      {documents !== null && (
        <Card className="mb-4" small>
          <CardBody className="p-0">
            {!documents.length ? (
              <p className="p-3 m-0" style={mutedStyle}>
                No se encontraron documentos para estos filtros.
              </p>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Document ID</th>
                    <th style={headerCellStyle}>Archivo</th>
                    <th style={headerCellStyle}>Fecha</th>
                    <th style={headerCellStyle}>Procesadora</th>
                    <th style={headerCellStyle}>Tipo de documento</th>
                    <th style={headerCellStyle}>Credencial</th>
                    <th style={headerCellStyle}>Parser</th>
                    <th style={headerCellStyle}>API</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((document) => {
                    const parserDocs = forDocument('parserDocuments', document.id);
                    const credential = credentialsById[document.credentialId];
                    return (
                      <tr key={document.id}>
                        <td style={cellStyle}>
                          <a
                            href={`/support/document_trace?documentId=${encodeURIComponent(document.id)}`}
                            rel="noopener noreferrer"
                            style={monoStyle}
                            target="_blank"
                          >
                            {document.id}
                          </a>
                        </td>
                        <td style={fileNameCellStyle} title={document.documentStorageRef || ''}>
                          {documentFileName(document.documentStorageRef)}
                        </td>
                        <td style={cellStyle}>
                          <div>{formatDate(document.date)}</div>
                        </td>
                        <td style={cellStyle} title={document.infoProvider || ''}>
                          {Translator.infoProvider(document.infoProvider) || '-'}
                        </td>
                        <td style={cellStyle}>
                          {document.documentType || '-'}
                          {document.relatedDocumentId && (
                            <div
                              style={mutedStyle}
                              title={`Original: ${document.relatedDocumentId}`}
                            >
                              transformado
                            </div>
                          )}
                        </td>
                        <td style={cellStyle} title={document.credentialId || ''}>
                          {credential ? (
                            credential.username
                          ) : (
                            <span style={monoStyle}>{shortId(document.credentialId)}</span>
                          )}
                        </td>
                        <td style={cellStyle}>
                          <PipelineBar
                            error={parserError}
                            segments={parserSegments(
                              document,
                              parserDocs,
                              forDocument('parserFails', document.id)
                            )}
                          />
                        </td>
                        <td style={cellStyle}>
                          <PipelineBar
                            error={apiError || (parserError && 'Sin datos de parser')}
                            segments={apiSegments(
                              document,
                              parserDocs,
                              forDocument('payments', document.id),
                              forDocument('paymentFails', document.id)
                            )}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      )}

      {documents !== null && (page > 1 || documents.length === PAGE_SIZE) && (
        <Row className="mb-4">
          <Col className="d-flex align-items-center">
            <Button
              className="mr-2"
              disabled={page === 1 || loading}
              onClick={() => setUrlState({ page: page - 1 })}
              outline
            >
              Anterior
            </Button>
            <span className="mr-2" style={mutedStyle}>
              Página {page}
            </span>
            <Button
              disabled={documents.length < PAGE_SIZE || loading}
              onClick={() => setUrlState({ page: page + 1 })}
              outline
            >
              Siguiente
            </Button>
          </Col>
        </Row>
      )}
    </Container>
  );
}
